// The one artifact R-4 asks for: every code the platform catalogue exports,
// each carrying a decision or a live dated waiver. Reviewed in a PR like
// code, and gated by src/i18n/errorDecisions.test.ts.
//
// Transcribed 2026-08-31 from the two generated SSOT pages -- Confluence
// 5421826080 (141 HTTP respCode codes) and 5421694990 (54 transaction
// errorCode codes), both rendered from docs/error-codes/error-codes.yaml in
// smobilpay-core, which this project cannot read directly. The counts were
// cross-checked against handover/PLATFORM-Handover-Error-Code-Customer-Copy-
// Gaps.md and agree exactly: 195 codes, 34 needing copy. 43017, 90004 and
// 94000 were added 2026-10-05 from smobilpay-error-registry codes.yaml
// v1.8.0, which this project can now read: 198 platform codes.
//
// ⚠ NINE codes here are SHORT and look wrong: 10, 11, 12, 501, 502, 503,
// 4209, 5000 and 5010. They are real rows on page 5421826080 -- 10/11/12 are
// Core precheck/session/balance errors and 501/502/503/5000/5010 are
// S3P-owned. They are NOT the service-code prefixes the legend lists, and an
// adversarial review already mistook them for exactly that. Removing them
// takes the respCode count to 132 and breaks the handover's measured 141.

// GENERIC: reviewed, and the payer is deliberately shown the generic message.
// Recorded so "generic by choice" cannot be mistaken for a forgotten OVERRIDE.
export type ErrorDecisionState = "PASSTHROUGH" | "OVERRIDE" | "GENERIC";

export type ErrorWaiver = {
  signedBy: string;
  signedOn: string;
  // R-8: the load-bearing field. A waiver without an expiry is a permanent
  // silent exemption, which is the outcome R-7 exists to prevent.
  expiresOn: string;
  reason: string;
};

export type ErrorDecision = {
  code: string;
  // Absent means not yet reviewed -- R-1's third state. It is an absence
  // rather than a "PENDING" member so a row cannot claim to be reviewed by
  // carrying a value.
  state?: ErrorDecisionState;
  // R-5: what a PASSTHROUGH was approved against. Without it an upstream
  // reword silently voids the review.
  approvedAgainst?: string;
  // Who approved a PASSTHROUGH or GENERIC and when. A waiver records its
  // signer; a decision about what a payer reads needs the same, or
  // "reviewed" is a claim with nobody behind it.
  reviewedBy?: string;
  reviewedOn?: string;
  // R-9's second drift trigger. A row overridden FROM BLANK has no baseline
  // to record, so it is marked rather than left as an empty string -- when
  // SSOT text later appears for it, our override needs reconciling.
  overriddenFromBlank?: boolean;
  waiver?: ErrorWaiver;
};

// Moved here from constants/query.ts, which owns cache keys and never owned
// this. The school-fee codes are CPS's own namespace, not the platform
// catalogue's numeric one; both appear in the table below because the
// resolver looks up whichever code the envelope carried.
// A lapsed payer-search session. It is the ONE refusal on this route the
// journey RECOVERS from rather than reports -- the fee stands and searching
// again fixes it, which is the only mitigation a 300-second window leaves a
// slow reader (D-HARM-5) -- so the template has to branch on it by value.
//
// Named here rather than in the template because `constants/` sits below
// `components/` in the dependency law: the template imports this, never the
// reverse. It was typed out in three places before, which is one renumbering
// away from a recovery that silently stops firing.
export const LAPSED_SESSION_CODE = "CPS-408-101";

export const SCHOOL_FEE_ERROR_CODES = [
  "CPS-401-001",
  "CPS-403-109",
  LAPSED_SESSION_CODE,
  "CPS-404-101",
  "CPS-404-104",
  "CPS-404-105",
  "CPS-422-102",
  "CPS-422-103",
  "CPS-422-105",
  "CPS-422-109",
  "CPS-422-110",
  // A registration payment must equal the resolved fee exactly.
  "CPS-422-111",
  // No fee chosen. Which fee the payer is paying is the one part of the
  // payload a server cannot infer, so it says so rather than guessing.
  "CPS-422-112",
  // Above the SCHOOL's own per-payment ceiling -- a different condition from
  // CPS-422-103, which is about the fee, and so a different code.
  "CPS-422-115",
  // NEW (MPAY-32322): the pupil named disagrees with the pupil the search
  // session resolved. Refused before any charge; the underlying fault it
  // closes was a DOUBLE CHARGE, not a wrong attribution.
  "CPS-422-116",
  // ⛔ CPS-501-101 RETIRED here. It was the governed 501 for a stubbed
  // capability, and its only emitter was the school-fee pay leg's
  // ErrPayLegNotWired, deleted by MPAY-32232. Measured on the Go integration
  // branch: `CodeNotImplemented` is declared and emitted from nowhere. A code
  // no path can send is copy a payer can never read.
] as const;

// The codes the MERGED pay route can send EITHER journey (MPAY-32394). They
// are not school-fee codes and they are not platform codes: two are minted by
// the harmonized route and three split conditions that used to be reported
// generically, and all five became reachable on deposit the day the two legs
// shared one door. Their copy therefore lives in the GENERAL `errors`
// namespace, which the resolver checks first, and never in `schoolfee.errors`
// as well -- a code in both would have the general entry shadow the specific
// one in silence.
export const SHARED_PAY_ERROR_CODES = [
  // The service selector names nothing payable: unknown, inactive, or
  // configured so that no journey can be derived from it.
  "CPS-403-121",
  // The everyday double-tap, inside the idempotency window. It read as a
  // generic error while the catalogue held a precise, reassuring sentence.
  "CPS-409-105",
  // A malformed amount. NEW -- it replaces the off-boundary use of
  // CPS-422-103, whose own wording and meaning are unchanged.
  "CPS-422-117",
  // The payment method is not available for the service. NEW -- it replaces
  // the generic CPS-422-105, whose wording is likewise unchanged.
  "CPS-422-118",
  // A field present that the derived journey forbids. NEW -- split out of
  // CPS-422-110, which keeps meaning "a required field is missing".
  "CPS-422-119",
] as const;

// Codes any request can receive before a journey is known -- the portal
// context resolver sends general validation failures ahead of both journeys.
// Not the pay route's list above, so they have their own; the copy lives in
// the GENERAL `errors` namespace for the same reason.
export const JOURNEY_NEUTRAL_ERROR_CODES = [
  // General request validation, taking over from CPS-422-105, which keeps
  // only its roster-row meaning. Same payer wording, so the move is invisible.
  "CPS-422-129",
] as const;

// ⛔ RULED 2026-08-31. The 51 transaction codes that carry agreed EN and FR
// catalogue text are PASSTHROUGH; every other platform code is waived with a
// staggered expiry.
//
// Why those 51 and not all 159 with agreed copy: they are the codes a payer
// actually meets, because they arrive in the transaction object's errorCode
// field on a failed payment -- payer-reachable BY CONSTRUCTION rather than by
// a predicate someone has to keep correct, which is the failure mode R-7
// rejected reachability for. OQ-4 says reviewed catalogue copy renders, and
// for these the platform team has agreed and published it in both languages.
//
// ⚠ Without this ruling every failed payment would have read "Something went
// wrong" instead of the provider's own reason -- reversing 16-06 and 16-12 on
// the day they landed. The generic-copy cost is confined to codes a payer
// rarely meets.
//
// ⛔ RULED 2026-10-05/06 (MPAY-33408): every other platform code now has a
// permanent decision and no waiver is left. Ten more are PASSTHROUGH; eighteen
// are OVERRIDE with wording the maintainer approved, 91000 among them and
// worded so it never says why a payment stopped; the rest are GENERIC, which
// means reviewed, with the payer shown the generic message on purpose. A
// waiver stays the tool for a code still under review, and it must expire.
//
// ⛔ An override for a platform code is written only from a maintainer ruling,
// never on our own initiative: the platform team owns the catalogue, and
// portal wording is a second source of truth to keep as small as possible.
export const ERROR_DECISIONS: ErrorDecision[] = [
  {
    code: "CPS-401-001",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-403-109",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-404-101",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-404-104",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-404-105",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-102",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-103",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-105",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-109",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-110",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  // MPAY-30898 / 12-08. The five refusals Journey A made reachable, all
  // OVERRIDE for the same reason as the rows above: the wording is reviewed
  // beside the screen it appears on. CPS-408-101's copy is doing more work
  // than the others -- it is the only one that tells the payer their fee
  // selection survives, which is what stops the expiry feeling like starting
  // over.
  //
  // ⛔ The CPS-501-101 row was DELETED here, not left as a spare. Its only
  // emitter went with MPAY-32232, and a decision row for a code nothing can
  // send is a claim that something was reviewed for a payer who will never
  // see it.
  {
    code: LAPSED_SESSION_CODE,
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-111",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-112",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-115",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-116",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  // MPAY-32394. OVERRIDE, matching all eleven rows above and D-22's stated
  // intent: the portal's own wording wins so it matches the screen around it
  // and cannot change without a portal release. The catalogue's reviewed
  // customer text is the source the copy was written from, not the thing
  // rendered -- rendering it verbatim is PASSTHROUGH, which is a different
  // decision and is not the one taken here.
  {
    code: "CPS-403-121",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-409-105",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-117",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-118",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "CPS-422-119",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  // OVERRIDE with CPS-422-105's reviewed wording, so a payer reads the same
  // sentence before and after the backend moves to this code.
  {
    code: "CPS-422-129",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "10",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "11",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "12",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "501",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "502",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "503",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43017",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "4209",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "5000",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "5010",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40001",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40002",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40003",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40004",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40005",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40006",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40007",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40008",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40009",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40010",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40011",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40012",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40013",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40014",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40015",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40016",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40017",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40018",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40019",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40020",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40021",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40022",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40201",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40202",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40203",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40301",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40302",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40303",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40304",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40305",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40306",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40400",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40401",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40402",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40403",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40404",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction rejected: You entered an incorrect amount. Please correct and try again.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40405",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction rejected: The amount entered is too small for the transaction. Please correct and try again.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40406",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "40407",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "40408",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40409",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction rejected: Amount is too small or too high for collection of this service",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40410",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40411",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40412",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40413",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40414",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40415",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40416",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40417",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40501",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40502",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "40602",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40603",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40604",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40605",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40606",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40607",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40608",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40609",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40610",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40611",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40612",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40613",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40614",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40615",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40616",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40617",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40618",
    state: "PASSTHROUGH",
    approvedAgainst:
      "An error occured during authorization of your payment. Please try again. If this error persists, please contact our support team for investigation.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40619",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40620",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40621",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40701",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "40702",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "41001",
    state: "PASSTHROUGH",
    approvedAgainst:
      "We are sorry. The service provider is currently unavailable. We are working to restore the service. Please try again later.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "41002",
    state: "PASSTHROUGH",
    approvedAgainst:
      "We are sorry. The service provider is currently unavailable. We are working to restore the service. Please try again later.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "41003",
    state: "PASSTHROUGH",
    approvedAgainst:
      "We are sorry. The service provider is currently unavailable. We are working to restore the service. Please try again later.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "41004",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "42001",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "42002",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "42003",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "42004",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43001",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43002",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43003",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43004",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43005",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43006",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Invalid phone number format",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43007",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Phone number not supported",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43008",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43009",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43010",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43011",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43012",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43013",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43014",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43015",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "43016",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "44001",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "44002",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "44003",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "44004",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "44005",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "45000",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "50000",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "50001",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The server is temporarily unavailable. Please try again later or contact your support team.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "50002",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "60001",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "60002",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "60003",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "60010",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "60020",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "60021",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "60022",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "70001",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "70101",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "70201",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "70202",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "70203",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "70204",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "70301",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "80001",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "80002",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "80003",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "90000",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "90001",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "90002",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "90003",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "90004",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  // Screening stopped the payment. Wording ruled 2026-10-06; it must never
  // say why (anti-tipping-off), and the platform has no text for it.
  {
    code: "91000",
    state: "OVERRIDE",
    overriddenFromBlank: true,
  },
  {
    code: "91100",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "91200",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "92000",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "93000",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "94000",
    state: "GENERIC",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-10-05",
  },
  {
    code: "702000",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed due to a general payment error. Contact support.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "702010",
    state: "PASSTHROUGH",
    approvedAgainst:
      "An unknown API version encountered during communication with the service provider. This may be due to an uncommunicated change by the service provider.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "702020",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The service provider reported the IP address making the request as unknown.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "702031",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "702100",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed when initializing communication with the service provider. Contact support.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "702101",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "702102",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The transaction was rejected because the amount is below the acceptable threshold",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "702103",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The transaction was rejected because the amount is above the acceptable threshold",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "702104",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "702105",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The transaction has timed out at the level of service provider",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "702106",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed. The Service Provider could not be reached temporarily. (during transaction initialization phase)",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "702107",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed. Login on Service Provider temporarily not possible.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "702401",
    state: "OVERRIDE",
    overriddenFromBlank: true,
  },
  {
    code: "703000",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed. Generic business error. Contact support.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703003",
    state: "PASSTHROUGH",
    approvedAgainst:
      "No phone number is associated to this subscriber.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703020",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed. The service provider could not be reached temporarily (during transaction initialization phase)",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703100",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Invalid input data sent in the request - one or more fields of the payment input request are invalid and were rejected",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703101",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed. Generic business error. Contact support.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703102",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The service provider has rejected the transaction",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703103",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Recipient account with service provider is blocked",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703104",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Sender account with service provider is blocked",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703105",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Service provider does not know the recipient (account) of the transaction",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703106",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "703107",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Recipient account does not have sufficient funds with the service provider to perform this transaction.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703108",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Initiator (sender) account does not have sufficient funds with the service provider to perform this transaction",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703109",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Service provider rejected the transaction because the amount is below the threshold allowed",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703110",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Service provider rejected the transaction because the amount is above the threshold allowed",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703111",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "703112",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Recipient account has exceeded the limits (daily, weekly, monthly etc) with the service provider",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703113",
    state: "PASSTHROUGH",
    approvedAgainst:
      "This Item (e.g. for bill payments) is no longer available for payment or has already been paid",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703114",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The service provider has rejected the transaction amount. Invalid amount in the transaction",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703115",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The service provider does not support the currency in the transaction",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703116",
    state: "OVERRIDE",
    overriddenFromBlank: true,
  },
  {
    code: "703117",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The service provider does not support the account number in the transaction.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703201",
    state: "PASSTHROUGH",
    approvedAgainst:
      "This transaction requires a customer authorization to complete. This authorization has not been given by customer via a confirmation.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703202",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The customer has rejected the payment or denied the approval of the transaction. The transaction cannot complete without the customer approval.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703203",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The customer has entered wrong or invalid authorization code for this transaction (e.g. wrong/invalid PIN, expired OTP, etc.). The transaction has been rejected by the Service Provider. The transaction cannot complete without the customer approval.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703220",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Service provider could not find the recipient (account) of the transaction",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703401",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed. Generic technical error. Contact support.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703501",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed. Generic technical error. Contact support.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "703503",
    state: "PASSTHROUGH",
    approvedAgainst:
      "This service is temporarily unavailable due to service maintenance.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "704000",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction failed. Generic technical error. Contact support.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "704001",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "704002",
    state: "OVERRIDE",
    overriddenFromBlank: false,
  },
  {
    code: "704003",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Transaction processing error. Contact support",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "704004",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The time between searching for and paying for an item was too long and the transaction has expired. Please retry from start.",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "704005",
    state: "PASSTHROUGH",
    approvedAgainst:
      "The service provider system could not be reached temporarily (during transaction processing phase)",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "704006",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Unknown response provided by service provider. Contact Support",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "704010",
    state: "OVERRIDE",
    overriddenFromBlank: true,
  },
  {
    code: "705000",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Unexpected technical error. Contact Support",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "705010",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Timeout during communication with service provider. Contact support",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "705020",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Timeout during communication with service provider. Could not contact service provider. Contact support",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "705030",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Timeout during communication with service provider. Could not contact service provider. Contact Support",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
  {
    code: "705031",
    state: "PASSTHROUGH",
    approvedAgainst:
      "Timeout during communication with service provider. Try again in 5 - 15 minutes",
    reviewedBy: "SCHOOLPAY maintainer",
    reviewedOn: "2026-08-31",
  },
];

const BY_CODE = new Map(ERROR_DECISIONS.map(d => [d.code, d]));

// The resolver's single question: may this code's upstream text be shown?
export const errorDecision = (code?: string): ErrorDecision | undefined =>
  code ? BY_CODE.get(code) : undefined;

export default ERROR_DECISIONS;
